// src/services/mediaUploadService.js
//
// REPLACEMENT for the 20-line version. Drop-in: `uploadPortalMedia` still
// returns a URL string, so the three call sites in LayoutBuilder.js need no
// edits.
//
// WHAT WAS WRONG
// --------------
// Every upload went to
//
//   portalLayouts/{layoutId}/{topicSlug}/{mediaType}/{Date.now()}_{fileName}
//
// which had three consequences, none visible at upload time:
//
//   1. The path is scoped to ONE layout, so a video uploaded for VA 3rd
//      science could never be seen by MD, DC, DE or NY — even though all five
//      carry the same slug.
//   2. That prefix is not in the FOLDERS list of the indexStorageMedia Cloud
//      Function, so the file never entered `mediaLibrary` and never showed up
//      in any picker.
//   3. It is not in match-media-to-topics.mjs's FOLDERS either, and the
//      Date.now() prefix would have wrecked the filename score even if it had
//      been, so the matcher could never find it.
//
// The builder's own upload button produced files nothing else could see.
//
// WHAT CHANGED, AND WHAT DELIBERATELY DID NOT
// -------------------------------------------
// Video and song now go to the shared folders — videos/ and music/ — which is
// where the ~600 finished assets live and what every other tool reads. A
// shared asset has to sit at a shared path to propagate.
//
// Images do NOT. They stay under a per-topic prefix, because most image
// uploads in this app are per-QUESTION: handleQuestionFileUpload in
// LayoutBuilder sends them through here too. A flat shared folder would name
// every question image in a topic identically and have them overwrite each
// other. Topic-level image sharing happens through topicLibrary.imageUrl
// instead, which is a reference rather than a path convention.

import {
  getDownloadURL,
  getMetadata,
  ref,
  uploadBytes,
} from "firebase/storage";
import { storage } from "../firebase/firebaseConfig";

// Video and music must match FOLDERS in functions/mediaIndex.js and in
// scripts/match-media-to-topics.mjs. A folder added here and nowhere else
// recreates the invisible-upload problem described above.
const SHARED_FOLDERS = { video: "videos", music: "music" };

const EXTENSIONS = {
  video: /\.(mp4|mov|webm|m4v)$/i,
  music: /\.(mp3|wav|m4a|ogg)$/i,
  image: /\.(png|jpe?g|webp|gif|svg)$/i,
};

const sanitize = (name) =>
  String(name || "upload").replace(/[^a-zA-Z0-9.-]/g, "_");

const extensionOf = (file) =>
  (String(file?.name || "").match(/\.[^.]+$/) || [""])[0].toLowerCase();

/**
 * The re-recorded assets are all named <slug>2025song.mp3 and
 * <slug>2025video.mp4, and both match-media-to-topics.mjs and the seeder's
 * tiebreak depend on that convention. Following it keeps new uploads legible
 * to tooling that already exists.
 *
 * `fileKey` (from fileKeyOf in src/utils/topicKey.js) overrides the slug.
 * Without it, `earth'swater` and `earthswater` both strip to `earthswater`
 * and the second topic's upload collides with the first topic's file.
 */
export const suggestFileName = ({ topicSlug, fileKey, mediaType, file }) => {
  const slug =
    String(fileKey || "").toLowerCase().replace(/[^a-z0-9_-]/g, "_") ||
    String(topicSlug || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  const ext = extensionOf(file);

  if (!slug || !SHARED_FOLDERS[mediaType]) return sanitize(file?.name);

  const year = new Date().getFullYear();
  return `${slug}${year}${mediaType === "music" ? "song" : "video"}${ext}`;
};

/**
 * Does something already live at this path?
 *
 * The old per-layout path carried a timestamp, so collisions were impossible.
 * A shared folder has no such protection, and silently overwriting
 * videos/motion2025video.mp4 would change that video for the ~70 layouts using
 * it. Checked before every shared write.
 */
export const mediaFileExists = async (path) => {
  try {
    await getMetadata(ref(storage, path));
    return true;
  } catch (error) {
    if (error?.code === "storage/object-not-found") return false;
    throw error;
  }
};

/**
 * Uploads and returns { url, path, name, shared }.
 *
 * Throws on collision unless `overwrite` is true. The thrown error carries
 * `code: "sbl/file-exists"` and the path, so a caller can turn it into a
 * confirm step rather than a failure. In a shared folder the blast radius of
 * an overwrite is every topic pointing at that filename, so that decision has
 * to be made consciously.
 */
export const uploadPortalMediaDetailed = async ({
  file,
  layoutId,
  topicSlug,
  fileKey,
  mediaType = "video",
  fileName,
  overwrite = false,
}) => {
  if (!file) throw new Error("No file selected.");

  const pattern = EXTENSIONS[mediaType];
  const shared = Boolean(SHARED_FOLDERS[mediaType]);

  const name = sanitize(
    fileName ||
      (shared ? suggestFileName({ topicSlug, fileKey, mediaType, file }) : file.name)
  );

  if (pattern && !pattern.test(name)) {
    // An .mp4 sitting in a musicUrl field is not a preference, it is a bug —
    // and there are live examples of exactly that today. Cheaper to refuse the
    // upload than to detect it three layers downstream.
    throw new Error(`"${name}" is not a valid ${mediaType} file.`);
  }

  let path;

  if (shared) {
    path = `${SHARED_FOLDERS[mediaType]}/${name}`;

    if (!overwrite && (await mediaFileExists(path))) {
      const error = new Error(
        `${path} already exists. Pick it from the library instead, rename ` +
          `this file, or confirm the overwrite — replacing it changes the ` +
          `media for every topic already using that file.`
      );
      error.code = "sbl/file-exists";
      error.path = path;
      throw error;
    }
  } else {
    // Per-question images. Timestamped, because two questions in one topic
    // would otherwise produce the same filename and silently overwrite.
    const scope = topicSlug || "general";
    path = `topic-images/${layoutId || "library"}/${scope}/${Date.now()}_${name}`;
  }

  const fileRef = ref(storage, path);
  await uploadBytes(fileRef, file);
  const url = await getDownloadURL(fileRef);

  return { url, path, name, shared };
};

/**
 * Original signature, unchanged: returns the URL string.
 *
 * Kept string-returning on purpose. LayoutBuilder.js calls this in three
 * places as `const url = await uploadPortalMedia(...)` and passes the result
 * straight into updateTopic(). Returning an object here would store
 * "[object Object]" in videoUrl with no error raised anywhere — a silent
 * corruption that would surface as a dead player days later. New code should
 * call uploadPortalMediaDetailed instead.
 */
export const uploadPortalMedia = async (args) => {
  const result = await uploadPortalMediaDetailed(args);
  return result.url;
};

export default uploadPortalMedia;
